import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import './ErrorBoundary.css';

interface ErrorBoundaryProps {
    children: ReactNode;
    /** Optional custom fallback; receives the error and a reset callback. */
    fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
    error: Error | null;
}

function reload() {
    window.location.reload();
}

/** True for Vite's dynamic-import failures after a deploy replaced hashed chunks. */
// eslint-disable-next-line react-refresh/only-export-components
export function isChunkLoadError(error: unknown): boolean {
    const msg = error instanceof Error ? error.message : String(error ?? '');
    return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError/i.test(msg);
}

/**
 * Friendly full-screen fallback. Used by ErrorBoundary and safe to render on its
 * own for Suspense/lazy failures (e.g. a stale chunk after a new deploy).
 */
export function RouteErrorFallback({ error }: { error?: unknown }) {
    const stale = isChunkLoadError(error);
    return (
        <div className="error-screen" role="alert">
            <div className="error-screen-icon">
                <AlertTriangle size={40} aria-hidden="true" />
            </div>
            <h1 className="error-screen-title">{stale ? 'A new version is available' : 'Something went wrong'}</h1>
            <p className="error-screen-text">
                {stale
                    ? 'The portal was updated while you were using it. Reload to get the latest version.'
                    : "Sorry about that. Reloading the page usually fixes it. If it keeps happening, let the committee know."}
            </p>
            <div className="error-screen-actions">
                <button type="button" className="btn btn-primary" onClick={reload}>
                    <RefreshCw size={16} aria-hidden="true" /> Reload
                </button>
                <a className="btn btn-secondary" href="/">Go to home</a>
            </div>
        </div>
    );
}

/**
 * Catches render errors below it, reports them to Sentry (a no-op when Sentry
 * isn't initialised) and shows a friendly screen instead of a blank page.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = { error: null };

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { error };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        try {
            Sentry.captureException(error, { contexts: { react: { componentStack: info.componentStack ?? '' } } });
        } catch {
            // Sentry not initialised or blocked; nothing else to do.
        }
        console.error('Render error caught by ErrorBoundary', error, info.componentStack);
    }

    reset = () => {
        this.setState({ error: null });
    };

    render() {
        const { error } = this.state;
        if (error) {
            return this.props.fallback ? this.props.fallback(error, this.reset) : <RouteErrorFallback error={error} />;
        }
        return this.props.children;
    }
}
